// src/components/TrialBanner.jsx
// Shown for the entire trial period so users can purchase anytime, not just
// during the final 7-day warning window.
//   - isWarning=true  (7 days or fewer left): full urgency banner, color
//     escalates as expiry approaches (unchanged behavior).
//   - isWarning=false (more than 7 days left, i.e. days 1-7 of a 14-day
//     trial): compact, low-key persistent bar with a plain "Upgrade" button.
//
// This component no longer has its own plan-picker/checkout UI — clicking
// "Upgrade" calls the onUpgradeClick prop, which App.js uses to open the
// single, consistent UpgradeScreen (the same big screen shown post-expiry)
// as an overlay. This keeps exactly one purchase experience everywhere in
// the app instead of two different UIs.

import React, { useState } from 'react';

// Blink animation for the low-key "upgrade anytime" message — same
// inject-once-into-<head> pattern already used elsewhere in the app
// (e.g. App.js's successBlinkStyle) for consistency.
// Color is redeclared at every keyframe step (not just opacity) so the
// animation itself can never drift to an inherited/default color mid-blink.
const trialBannerBlinkStyle = `
@keyframes trialBannerBlink {
    0%, 100% { opacity: 1; color: #22d3ee; }
    50% { opacity: 0.35; color: #22d3ee; }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('trial-banner-blink-style')) {
    const styleTag = document.createElement('style');
    styleTag.id = 'trial-banner-blink-style';
    styleTag.innerHTML = trialBannerBlinkStyle;
    document.head.appendChild(styleTag);
}

export default function TrialBanner({ daysLeft, expiresAt, isWarning = true, onUpgradeClick }) {
  // Dismiss persistence differs by banner type:
  //   - isWarning=true  (urgent, final 7 days): dismiss persists for the whole
  //     browser session via sessionStorage — unchanged original behavior.
  //   - isWarning=false (low-key "upgrade anytime" nudge): dismiss is NOT
  //     persisted to storage at all. This component remounts fresh on every
  //     login (App.js renders <LoginPage> instead while logged out, which
  //     unmounts this), so a plain in-memory useState(false) naturally means
  //     "dismissed for this login, shown again next time they log in" —
  //     exactly the desired behavior for a soft nudge instead of an alert.
  const dismissStorageKey = isWarning ? 'trial-banner-dismissed' : null;
  const [dismissed, setDismissed] = useState(
    !!dismissStorageKey &&
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(dismissStorageKey) === 'true'
  );

  if (dismissed) return null;

  // Color escalates as expiry approaches — only used in the full urgency banner
  const isUrgent = daysLeft <= 2;
  const isMedium = daysLeft <= 5;
  const urgentBg = isUrgent ? '#dc2626'
                 : isMedium ? '#ea580c'
                 : '#0891b2';

  // Compact bar (isWarning=false) uses a calm, low-key color regardless of daysLeft
  const bg = isWarning ? urgentBg : '#1e293b';

  const formattedDate = expiresAt instanceof Date
    ? expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  let message;
  if (isWarning) {
    if (daysLeft === 0)      message = 'Your trial ends today.';
    else if (daysLeft === 1) message = 'Your trial ends tomorrow.';
    else                     message = `Your trial ends in ${daysLeft} days (${formattedDate}).`;
  } else {
    // Low-key, no urgency — just makes purchasing discoverable from day one
    message = 'Like Quantum AI Shield? You can upgrade anytime.';
  }

  const handleDismiss = () => {
    if (dismissStorageKey) {
      try { sessionStorage.setItem(dismissStorageKey, 'true'); } catch {}
    }
    setDismissed(true);
  };

  return (
    <div style={{ position: 'relative', zIndex: 200 }}>
      {/* Main banner */}
      <div
        style={{
          backgroundColor: bg,
          color:           'white',
          padding:         isWarning ? '12px 20px' : '8px 20px',
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          flexWrap:        'wrap',
          gap:             '16px',
          fontSize:        isWarning ? 'clamp(13px, 1.1vw, 16px)' : 'clamp(12px, 1vw, 14px)',
          fontWeight:      isWarning ? 600 : 500,
          boxShadow:       '0 2px 4px rgba(0,0,0,0.15)',
          opacity:         isWarning ? 1 : 0.92,
        }}
      >
        <span
          style={!isWarning ? {
            color:      '#22d3ee',
            fontSize:   'clamp(15px, 1.3vw, 18px)',
            fontWeight: 700,
            animation:  'trialBannerBlink 1.2s ease-in-out infinite',
          } : undefined}
        >
          {isWarning ? `⏰ ${message}` : message}
        </span>

        {/* Upgrade button — opens the shared UpgradeScreen via App.js */}
        <button
          onClick={onUpgradeClick}
          style={{
            background:   isWarning
              ? 'linear-gradient(to right, #00ccff, #0077ff)'
              : 'transparent',
            color:        isWarning ? 'white' : '#22d3ee',
            border:       isWarning ? 'none' : '1px solid rgba(34,211,238,0.5)',
            padding:      isWarning ? '7px 18px' : '6px 16px',
            borderRadius: '6px',
            fontWeight:   isWarning ? 700 : 600,
            cursor:       'pointer',
            fontSize:     isWarning ? 'clamp(12px, 1vw, 14px)' : 'clamp(13px, 1.05vw, 15px)',
            boxShadow:    isWarning ? '0 2px 8px rgba(0,119,255,0.3)' : 'none',
          }}
        >
          {isWarning ? 'Purchase' : 'Upgrade'}
        </button>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          style={{
            background:   'transparent',
            color:        isWarning ? 'white' : '#ef4444',
            border:       isWarning ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(239,68,68,0.5)',
            padding:      '6px 14px',
            borderRadius: '6px',
            cursor:       'pointer',
            fontWeight:   isWarning ? 400 : 600,
            fontSize:     isWarning ? 'clamp(11px, 0.9vw, 13px)' : 'clamp(13px, 1.05vw, 15px)',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}